import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable, of } from 'rxjs';
import { map, tap, shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AdminDocumentsService {

  private apiUrl = environment.API_ADMIN + "Documents"

  private manualCache = new Map<string, Uint8Array>();
  private inflightManualRequests = new Map<string, Observable<Uint8Array>>();

  constructor(
    private http: HttpClient,
  ) { }

  getSoftwareManual(language?: string) {
    const langParam = language ? `?language=${language}` : '';
    return this.http.get(`${this.apiUrl}/software-manual${langParam}`);
  }

  getSoftwareManualUrl(language?: string) {
    const langParam = language ? `?language=${language}` : '';
    return `${this.apiUrl}/software-manual${langParam}`;
  }

  getSoftwareManualCached(language?: string): Observable<Uint8Array> {
    const cacheKey = language || 'default';
    const cached = this.manualCache.get(cacheKey);
    if (cached) {
      return of(this.cloneBytes(cached));
    }

    const inflight = this.inflightManualRequests.get(cacheKey);
    if (inflight) {
      return inflight.pipe(map(bytes => this.cloneBytes(bytes)));
    }

    const url = this.getSoftwareManualUrl(language);
    const request$ = this.http.get(url, { responseType: 'arraybuffer' }).pipe(
      map((buffer: ArrayBuffer) => new Uint8Array(buffer)),
      tap(bytes => this.manualCache.set(cacheKey, this.cloneBytes(bytes))),
      // Ensure multiple subscribers share the same request
      shareReplay(1)
    );

    this.inflightManualRequests.set(cacheKey, request$);

    return request$.pipe(map(bytes => this.cloneBytes(bytes)));
  }

  /** Clears the in-memory manual cache. */
  clearSoftwareManualCache(language?: string): void {
    if (language) {
      this.manualCache.delete(language);
      this.inflightManualRequests.delete(language);
    } else {
      this.manualCache.clear();
      this.inflightManualRequests.clear();
    }
  }

  private cloneBytes(bytes: Uint8Array): Uint8Array {
    return bytes.slice();
  }
}
